/**
 * BulkPortEditDialog -- ONE dialog that edits every selected port, on one switch or many.
 *
 * WHY (owner, 2026-09-14): "it is not easy to select multiple ports and name them and or edit
 * them". The port table (routes/SwitchPorts) selects; this edits.
 *
 * THE FORM. Every field starts at "No change" and only fields the operator sets are sent, per
 * port (lib/portTable.buildChangeGroups). Toolbar quick actions (Enable, Disable, PoE on/off)
 * open this same dialog pre-set, so there is exactly one write path to review.
 *
 * NAMES SAVE IMMEDIATELY (owner decision 2026-09-14, option C "Meraki auto-sync"). The name (same
 * for all, or a pattern with {port} / {switch}) is NOT part of the previewed CLI: "Save names"
 * sends every changed name in ONE batch request (lib/portNameSync.buildNameItems), Meridian holds
 * them at once, and the controller writes each switch's descriptions in the background -- the
 * table's badges then show Syncing -> Synced (or Waiting for switch / Failed). Names are
 * therefore never in the per-switch preview below, so they are never sent to a switch twice.
 *
 * PREVIEW, GROUPED PER SWITCH. Preview renders each switch's exact command set (dry_run: no
 * device contact) and the access path Apply will take (direct / via gateway). The preview
 * remembers the request KEY it was rendered from; the form derives its key every render, and
 * Apply is offered only while they match -- the reactive-key pattern (S6a). Any edit, the save
 * checkbox, or a port's VLAN changing underneath invalidates it without anyone clearing state.
 * Apply sends the PREVIEWED groups, never a rebuild of the form.
 *
 * APPLY, SWITCH BY SWITCH, IN SEQUENCE. Each switch is one CLI session and one audit row.
 * FAILURE POLICY (decision):
 *   * relay refused / unreachable / transport error -> CONTINUE with the next switch. Those are
 *     facts about ONE switch's access path; the next switch may well be reachable, and stopping
 *     would leave the operator a half-done job for no safety gain.
 *   * the switch REFUSED a command ("% Invalid input") -> STOP. The remaining switches would get
 *     the same command set, land every line before the refused one, and refuse it too -- N
 *     partially-applied switches instead of one. The rest are reported "not run" with a
 *     "Run the remaining switches" button, for when the refusal was specific to that model.
 * Every switch gets its own honest result via lib/switchApplyResult: applied + saved / applied
 * but not saved (inline Save) / rejected (the refused line) / relay refused (plain words).
 *
 * UPLINK GUARD. If the selection includes an uplink or a port whose LLDP neighbour is a switch
 * or gateway, and the edit can cut or loop that link (disable, any VLAN change, edge port on,
 * 802.1X enforcement, PoE off -- lib/portTable.guardedEdits), Apply needs an explicit
 * confirmation tied to the current request key, or those ports can be left out in one click.
 *
 * ROLE: rendered by the page for msp_admin only; the backend enforces is_admin regardless.
 */
import { useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useSetPortNames, useSwitchPortBatch, type PortConfigResponse } from "@/lib/api/hooks";
import { ApiError } from "@/lib/api/client";
import { buildNameItems, describeNameSave } from "@/lib/portNameSync";
import {
  EMPTY_EDIT,
  buildChangeGroups,
  changeGroupsKey,
  describeRisk,
  expandNamePattern,
  guardedEdits,
  rowsNeedingConfirm,
  type BulkEdit,
  type PortRow,
  type SwitchChangeGroup,
} from "@/lib/portTable";
import {
  classifyApplyResult,
  describeApplyOutcome,
  relayRefusalReason,
  type ApplyResultKind,
  type OutcomeMessage,
} from "@/lib/switchApplyResult";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";
import {
  AccessPathLine,
  OutcomeNote,
  SaveRunningConfig,
  SaveToStartupCheckbox,
} from "@/components/site/SwitchSettingsDialog";
import { cn } from "@/lib/cn";

/** One switch's dry-run: the response, or why the request itself failed (400, 403, transport). */
interface PreviewItem {
  group: SwitchChangeGroup;
  res: PortConfigResponse | null;
  error: string | null;
}

/** A rendered preview and the exact request it came from. */
interface PreviewSnapshot {
  key: string;
  save: boolean;
  items: PreviewItem[];
}

/** One switch's real apply. */
interface SwitchRun {
  status: "running" | "done" | "skipped";
  res?: PortConfigResponse;
  kind?: ApplyResultKind;
  /** The request threw (not a switch result): 400/403/404/transport. */
  error?: string;
  note?: string;
}

interface RunState {
  /** The preview key this run executed. */
  key: string;
  bySerial: Record<string, SwitchRun>;
  /** Index of the first switch not run because of the stop-on-rejection policy. */
  stoppedAt: number | null;
  active: boolean;
}

/** Error text for a thrown request. */
function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** A refused name batch in plain words: the server's per-item problems when it sent them. */
function nameSaveError(e: unknown): string {
  if (e instanceof ApiError && e.body && typeof e.body === "object") {
    const body = e.body as { message?: string; errors?: { error: string }[] };
    const items = (body.errors ?? []).map((x) => x.error);
    if (body.message || items.length) return [body.message, ...items].filter(Boolean).join(" ");
  }
  return errorText(e);
}

/** "the gateway login..." -> "The gateway login....": sentence case with one full stop. */
function sentence(text: string): string {
  const t = text.trim();
  const s = t.charAt(0).toUpperCase() + t.slice(1);
  return /[.!?]$/.test(s) ? s : `${s}.`;
}

/** The outcome box for one finished switch. */
function runMessage(r: SwitchRun, save: boolean): OutcomeMessage | null {
  if (r.status !== "done") return null;
  if (r.error) return { tone: "critical", title: "The request did not reach the switch", detail: r.error };
  if (!r.res || !r.kind) return null;
  if (r.kind === "relay_refused") {
    return {
      tone: "critical",
      title: "Not applied — Meridian could not open a session through the site gateway",
      detail: sentence(relayRefusalReason(r.res)),
    };
  }
  return describeApplyOutcome(r.res, { saveRequested: save });
}

const selectCls = "min-h-touch w-full rounded border border-line bg-surface px-2 py-1.5 text-sm text-ink focus:border-brand focus:outline-none";
const inputCls = selectCls;

export function BulkPortEditDialog({
  rows,
  preset,
  onClose,
  onExclude,
}: {
  /** The selected ports (current data). */
  rows: PortRow[];
  /** Initial field values (toolbar quick actions); everything else starts at No change. */
  preset?: Partial<BulkEdit>;
  onClose: () => void;
  /** Remove these row keys from the selection (the uplink guard's "leave them out"). */
  onExclude: (keys: string[]) => void;
}) {
  const batch = useSwitchPortBatch();
  const saveNames = useSetPortNames();
  /** The last name save's summary line ("6 names saved · syncing to 2 switches"). */
  const [nameResult, setNameResult] = useState<string | null>(null);
  const [edit, setEdit] = useState<BulkEdit>({ ...EMPTY_EDIT, ...preset });
  const [save, setSave] = useState(true);
  const [preview, setPreview] = useState<PreviewSnapshot | null>(null);
  const [previewing, setPreviewing] = useState(false);
  /** The request key the operator confirmed the uplink warning for. */
  const [confirmedKey, setConfirmedKey] = useState<string | null>(null);
  const [run, setRun] = useState<RunState | null>(null);

  /** Set one form field. */
  const set = <K extends keyof BulkEdit>(k: K, v: BulkEdit[K]) => setEdit((prev) => ({ ...prev, [k]: v }));

  // ---- derived every render: the request, its key, freshness, the guard -----------------
  const { groups, errors } = useMemo(() => buildChangeGroups(rows, edit), [rows, edit]);
  const key = changeGroupsKey(groups, save);
  const fresh = !!preview && preview.key === key;
  const guards = guardedEdits(edit);
  const risky = rowsNeedingConfirm(rows, edit);
  const confirmed = risky.length === 0 || confirmedKey === key;
  const previewBroken = !!preview && preview.items.some((i) => i.error || !i.res || i.res.error);
  const running = !!run?.active;
  const ranThisPreview = !!run && !!preview && run.key === preview.key;
  const switchCount = new Set(rows.map((r) => r.serial)).size;
  const untouched = errors.length === 1 && errors[0]?.startsWith("Nothing to change");
  /** NAMES: saved immediately through the batch name API, never part of the CLI preview. */
  const names = useMemo(() => buildNameItems(rows, edit), [rows, edit]);
  /** Only the Name field is set: "Nothing to change" is about the CLI part, so it is not shown. */
  const nameOnly = untouched && edit.nameMode !== "keep";

  /** Live name preview: what every selected port will be called. */
  const namePreview = useMemo(() => {
    if (edit.nameMode !== "set" || !edit.namePattern.trim()) return [];
    return [...rows]
      .sort((a, b) => a.switchName.localeCompare(b.switchName, undefined, { numeric: true }) || a.order - b.order)
      .map((r) => ({ key: r.key, switchName: r.switchName, number: r.number, name: expandNamePattern(edit.namePattern, { port: r.number, switchName: r.switchName }) }));
  }, [rows, edit.nameMode, edit.namePattern]);

  // ---- step 1: preview every switch (dry_run, parallel -- no device contact) --------------
  async function doPreview() {
    if (errors.length || !groups.length) return;
    const snapshot = { key, save, groups };
    setPreviewing(true);
    setRun(null);
    const items = await Promise.all(
      snapshot.groups.map(async (group): Promise<PreviewItem> => {
        try {
          const res = await batch.applyPorts(group.serial, { changes: group.changes, save: snapshot.save, dry_run: true });
          return { group, res, error: null };
        } catch (e) {
          return { group, res: null, error: errorText(e) };
        }
      }),
    );
    setPreview({ key: snapshot.key, save: snapshot.save, items });
    setPreviewing(false);
  }

  // ---- step 2: apply the PREVIEWED groups, switch by switch -----------------------------
  async function runFrom(start: number) {
    if (!preview || running) return;
    // A FIRST run needs a preview that still matches the form, and the uplink confirmation.
    // A CONTINUATION ("Run the remaining switches") runs the same reviewed snapshot the operator
    // already confirmed: the first switch's refetch legitimately changes the live rows (its new
    // VLAN, its names), which would otherwise make the untouched remainder un-runnable.
    const continuing = start > 0 && run?.key === preview.key;
    if (!continuing && (!fresh || !confirmed)) return;
    const snap = preview;
    const bySerial: Record<string, SwitchRun> = start > 0 && run ? { ...run.bySerial } : {};
    const publish = (active: boolean, stoppedAt: number | null) =>
      setRun({ key: snap.key, bySerial: { ...bySerial }, stoppedAt, active });

    for (let i = start; i < snap.items.length; i++) {
      const item = snap.items[i];
      if (!item) continue;
      const serial = item.group.serial;
      // A switch whose preview failed was never reviewed: it is not run.
      if (item.error || !item.res || item.res.error) {
        bySerial[serial] = { status: "skipped", note: "Not run: its preview failed." };
        publish(true, null);
        continue;
      }
      bySerial[serial] = { status: "running" };
      publish(true, null);

      let res: PortConfigResponse;
      try {
        res = await batch.applyPorts(serial, { changes: item.group.changes, save: snap.save, dry_run: false });
      } catch (e) {
        bySerial[serial] = { status: "done", error: errorText(e) };
        publish(true, null);
        continue; // request-level failure: specific to this switch -> continue
      }
      const kind = classifyApplyResult(res, { saveRequested: snap.save });
      const entry: SwitchRun = { status: "done", res, kind };
      void batch.refresh(serial); // one refetch per switch, after its apply
      bySerial[serial] = entry;

      // Stop-on-rejection policy (see the module header).
      if (kind === "rejected" && i < snap.items.length - 1) {
        for (let j = i + 1; j < snap.items.length; j++) {
          const rest = snap.items[j];
          if (rest) bySerial[rest.group.serial] = { status: "skipped", note: "Not run: stopped after a switch refused a command." };
        }
        publish(false, i + 1);
        return;
      }
      publish(true, null);
    }
    publish(false, null);
  }

  /** Save every changed name now (one batch request); the switches sync in the background. */
  function doSaveNames() {
    if (names.errors.length || !names.items.length || saveNames.isPending) return;
    setNameResult(null);
    saveNames.mutate(names.items, { onSuccess: (res) => setNameResult(describeNameSave(res)) });
  }

  const applyLabel =
    switchCount === 1 ? `Apply to ${rows[0]?.switchName ?? "switch"}` : `Apply to ${preview?.items.length ?? groups.length} switches`;

  return (
    <Dialog.Root open onOpenChange={(o) => !o && !running && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content
          aria-describedby={undefined}
          className="md-dialog fixed left-1/2 top-1/2 z-sheet flex max-h-[92vh] w-[calc(100%-2rem)] max-w-2xl -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg border border-line bg-overlay shadow-sheet outline-none"
        >
          {/* ------------------------------------------------------------------ header --- */}
          <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
            <div className="min-w-0">
              <Dialog.Title className="text-sm font-semibold text-ink">
                Edit <span className="tabular-nums">{rows.length}</span> port{rows.length === 1 ? "" : "s"}
              </Dialog.Title>
              <p className="mt-0.5 truncate text-xs text-ink-muted">
                {switchCount === 1 ? `on ${rows[0]?.switchName}` : `on ${switchCount} switches`} · every field starts at No change
              </p>
            </div>
            <Dialog.Close
              disabled={running}
              className="flex h-touch w-touch shrink-0 items-center justify-center rounded text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink disabled:opacity-50"
              aria-label="Close"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </Dialog.Close>
          </div>

          {/* ------------------------------------------------------------------ body ----- */}
          <div className="flex flex-col gap-5 overflow-y-auto px-5 py-4">
            {/* NAME */}
            <FormSection title="Name">
              <div className="grid gap-3 sm:grid-cols-[10rem,1fr]">
                <select className={selectCls} aria-label="Name" value={edit.nameMode} onChange={(e) => set("nameMode", e.target.value as BulkEdit["nameMode"])}>
                  <option value="keep">No change</option>
                  <option value="set">Set name</option>
                  <option value="clear">Clear name</option>
                </select>
                {edit.nameMode === "set" && (
                  <div className="flex flex-col gap-1.5">
                    <input
                      className={inputCls}
                      aria-label="Name or pattern"
                      placeholder="Room 10{port}"
                      value={edit.namePattern}
                      onChange={(e) => set("namePattern", e.target.value)}
                    />
                    <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-ink-subtle">
                      <span>Insert</span>
                      {["{port}", "{switch}"].map((token) => (
                        <button
                          key={token}
                          type="button"
                          className="rounded border border-line bg-surface px-1.5 py-0.5 font-mono text-brand hover:bg-surface-2"
                          onClick={() => set("namePattern", `${edit.namePattern}${token}`)}
                        >
                          {token}
                        </button>
                      ))}
                      <span>· no token = the same name on every port</span>
                    </div>
                  </div>
                )}
              </div>
              {namePreview.length > 0 && (
                <div className="max-h-40 overflow-auto rounded border border-line bg-surface">
                  <table className="w-full text-xs">
                    <tbody className="font-mono">
                      {namePreview.map((n) => (
                        <tr key={n.key} className="border-b border-line/60 last:border-0">
                          {switchCount > 1 && <td className="truncate px-2 py-1 font-sans text-ink-muted">{n.switchName}</td>}
                          <td className="w-12 px-2 py-1 text-right tabular-nums text-ink-muted">{n.number}</td>
                          <td className="px-2 py-1 text-ink-subtle">→</td>
                          <td className="px-2 py-1 text-ink">{n.name || <span className="text-ink-subtle">(empty)</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {/* Names save on their own, now -- no CLI preview (option C). */}
              {edit.nameMode !== "keep" && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <Button
                    variant="secondary"
                    disabled={!!names.errors.length || !names.items.length || saveNames.isPending || running}
                    onClick={doSaveNames}
                  >
                    {saveNames.isPending
                      ? "Saving…"
                      : names.items.length
                        ? `Save ${names.items.length} name${names.items.length === 1 ? "" : "s"}`
                        : "No name changes"}
                  </Button>
                  {nameResult && !saveNames.isPending && <span className="text-xs text-ink-muted">{nameResult}</span>}
                </div>
              )}
              {names.errors.length > 0 && (
                <ul className="flex flex-col gap-0.5 text-xs text-critical">
                  {names.errors.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
              )}
              {saveNames.error && <p className="text-xs text-critical">{nameSaveError(saveNames.error)}</p>}
              <p className="text-[11px] text-ink-subtle">
                Names sync to the switches automatically — saved in Meridian now, written to each switch as the
                port description in the background. A name changed on the switch itself is overwritten. Switches
                not assigned to a site keep the names in Meridian until they are assigned.
              </p>
            </FormSection>

            {/* PORT */}
            <FormSection title="Port">
              <div className="grid gap-3 sm:grid-cols-2">
                <Labeled label="Enabled">
                  <select className={selectCls} value={edit.enabled} onChange={(e) => set("enabled", e.target.value as BulkEdit["enabled"])}>
                    <option value="">No change</option>
                    <option value="up">Enable (no shutdown)</option>
                    <option value="down">Disable (shutdown)</option>
                  </select>
                </Labeled>
                <Labeled label="PoE">
                  <select className={selectCls} value={edit.poe} onChange={(e) => set("poe", e.target.value as BulkEdit["poe"])}>
                    <option value="">No change</option>
                    <option value="on">On</option>
                    <option value="off">Off</option>
                  </select>
                </Labeled>
                <Labeled label="Edge port (spanning-tree)" hint="For gateways, APs and end devices only. Never on a link to another switch.">
                  <select className={selectCls} value={edit.edgePort} onChange={(e) => set("edgePort", e.target.value as BulkEdit["edgePort"])}>
                    <option value="">No change</option>
                    <option value="on">On (edge port)</option>
                    <option value="off">Off</option>
                  </select>
                </Labeled>
                <Labeled label="802.1X port control">
                  <select className={selectCls} value={edit.dot1x} onChange={(e) => set("dot1x", e.target.value as BulkEdit["dot1x"])}>
                    <option value="">No change</option>
                    <option value="force-authorized">Force-authorized (always on)</option>
                    <option value="auto">Auto (802.1X)</option>
                    <option value="force-unauthorized">Force-unauthorized (block)</option>
                  </select>
                </Labeled>
                <Labeled label="LLDP">
                  <select className={selectCls} value={edit.lldp} onChange={(e) => set("lldp", e.target.value as BulkEdit["lldp"])}>
                    <option value="">No change</option>
                    <option value="both">TX &amp; RX</option>
                    <option value="tx">TX only</option>
                    <option value="rx">RX only</option>
                    <option value="off">Disabled</option>
                  </select>
                </Labeled>
              </div>
            </FormSection>

            {/* VLAN */}
            <FormSection title="VLAN">
              <div className="grid gap-3 sm:grid-cols-2">
                <Labeled label="Mode">
                  <select className={selectCls} value={edit.vlanMode} onChange={(e) => set("vlanMode", e.target.value as BulkEdit["vlanMode"])}>
                    <option value="">No change</option>
                    <option value="access">Access (one untagged VLAN)</option>
                    <option value="trunk">Trunk (native + tagged)</option>
                  </select>
                </Labeled>
                {edit.vlanMode === "access" && (
                  <Labeled label="Access VLAN" hint="The port's current VLAN is removed, so it ends up in exactly this one.">
                    <input className={inputCls} inputMode="numeric" placeholder="1000" value={edit.accessVlan} onChange={(e) => set("accessVlan", e.target.value)} />
                  </Labeled>
                )}
                {edit.vlanMode === "trunk" && (
                  <>
                    <Labeled label="Native VLAN" hint="Optional. Management is VLAN 100.">
                      <input className={inputCls} inputMode="numeric" placeholder="100" value={edit.nativeVlan} onChange={(e) => set("nativeVlan", e.target.value)} />
                    </Labeled>
                    <Labeled label="Tagged VLANs to add" hint="Comma-separated, ranges allowed.">
                      <input className={inputCls} placeholder="400, 601-603" value={edit.taggedVlans} onChange={(e) => set("taggedVlans", e.target.value)} />
                    </Labeled>
                    <Labeled label="VLANs to remove">
                      <input className={inputCls} placeholder="1" value={edit.removeVlans} onChange={(e) => set("removeVlans", e.target.value)} />
                    </Labeled>
                  </>
                )}
              </div>
            </FormSection>

            <SaveToStartupCheckbox checked={save} onChange={setSave} />

            {/* Validation. "Nothing to change" is a hint, not an error, until something is set. */}
            {errors.length > 0 && !nameOnly && (
              <ul className={cn("flex flex-col gap-0.5 text-xs", untouched ? "text-ink-subtle" : "text-critical")}>
                {errors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            )}

            {/* UPLINK GUARD */}
            {risky.length > 0 && (
              <div className="rounded border border-warn bg-warn-soft p-3 text-xs text-warn" role="alert">
                <div className="font-medium">
                  {risky.length} selected port{risky.length === 1 ? " links" : "s link"} to other network gear, and this edit will {guards.join(", ")}.
                </div>
                <p className="mt-1">
                  On these ports that can cut a switch off the network — including from Meridian — or loop it.
                </p>
                <ul className="mt-2 flex flex-col gap-0.5 font-mono">
                  {risky.map((r) => (
                    <li key={r.key}>
                      {r.switchName} port {r.number}: <span className="font-sans">{describeRisk(r)}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <Button variant="secondary" disabled={running} onClick={() => onExclude(risky.map((r) => r.key))}>
                    Leave these ports out
                  </Button>
                  <label className="flex min-h-touch items-center gap-2 text-ink">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-brand"
                      checked={confirmedKey === key}
                      onChange={(e) => setConfirmedKey(e.target.checked ? key : null)}
                    />
                    <span>I understand — include them</span>
                  </label>
                </div>
              </div>
            )}

            {/* PREVIEW / RESULTS, grouped per switch */}
            {preview && !fresh && !ranThisPreview && (
              <p className="text-xs text-ink-subtle">Changed since the preview — preview again before applying.</p>
            )}
            {preview && (fresh || ranThisPreview) && (
              <div className="flex flex-col gap-3">
                {preview.items.map((item) => {
                  const r = run?.key === preview.key ? run.bySerial[item.group.serial] : undefined;
                  const msg = r ? runMessage(r, preview.save) : null;
                  const shownAccess = r?.res?.access ?? item.res?.access;
                  return (
                    <section key={item.group.serial} className="rounded border border-line bg-surface p-3">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <h4 className="text-sm font-medium text-ink">{item.group.switchName}</h4>
                        <span className="font-mono text-[11px] text-ink-muted">
                          port{item.group.portNumbers.length === 1 ? "" : "s"} {item.group.portNumbers.join(", ")}
                        </span>
                      </div>
                      <AccessPathLine access={shownAccess} />
                      {/* Why an expected removal is absent, e.g. "VLAN 400 stays on port 4, tagged". */}
                      {item.group.notes.length > 0 && (
                        <ul className="mt-1 flex flex-col gap-0.5 text-[11px] text-ink-muted">
                          {item.group.notes.map((n, i) => (
                            <li key={i}>{n}</li>
                          ))}
                        </ul>
                      )}

                      {item.error ? (
                        <p className="mt-1 text-xs text-critical">{item.error}</p>
                      ) : item.res?.error ? (
                        <p className="mt-1 text-xs text-critical">{item.res.error}</p>
                      ) : (
                        !r && item.res && (
                          <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed text-ink-muted">
                            {item.res.commands.join("\n")}
                          </pre>
                        )
                      )}

                      {r?.status === "running" && <p className="mt-2 text-xs text-ink-muted">Applying…</p>}
                      {r?.status === "skipped" && <p className="mt-2 text-xs text-ink-muted">{r.note}</p>}
                      {msg && (
                        <div className="mt-2">
                          <OutcomeNote message={msg}>
                            {r?.kind === "unsaved" && <SaveRunningConfig serial={item.group.serial} compact />}
                          </OutcomeNote>
                        </div>
                      )}
                      {/* The refused line(s), attributed when the switch echo allowed it. */}
                      {r?.kind === "rejected" && r.res?.rejected && (
                        <ul className="mt-1 flex flex-col gap-0.5 font-mono text-[11px] text-critical">
                          {r.res.rejected.map((x, i) => (
                            <li key={i}>
                              {x.command ?? "(command not identified)"} — {x.error}
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                  );
                })}
              </div>
            )}
          </div>

          {/* ------------------------------------------------------------------ footer --- */}
          <div className="flex flex-wrap items-center gap-2 border-t border-line px-5 py-3">
            <Button variant="secondary" disabled={!!errors.length || previewing || running} onClick={() => void doPreview()}>
              {previewing ? "Rendering…" : fresh ? "Preview again" : "Preview"}
            </Button>
            <RoleGate min="msp_admin" mode="disable" reason="Applying switch config requires admin.">
              <Button
                variant="primary"
                disabled={!fresh || previewBroken || !confirmed || running || ranThisPreview}
                onClick={() => void runFrom(0)}
              >
                {running ? "Applying…" : applyLabel}
              </Button>
            </RoleGate>
            {run && run.stoppedAt !== null && ranThisPreview && (
              // Same guard as Apply: disabled while a run is active, so a double click cannot
              // start two sequential runs over the same switches (MR !137 review).
              <Button
                variant="secondary"
                disabled={running || previewing}
                onClick={() => void runFrom(run.stoppedAt ?? 0)}
              >
                {running ? "Applying…" : "Run the remaining switches"}
              </Button>
            )}
            <Button variant="ghost" disabled={running} onClick={onClose}>
              {run && !run.active ? "Done" : "Cancel"}
            </Button>
            <p className="w-full text-[11px] text-ink-subtle">
              Preview first. Apply runs exactly the previewed commands, one switch at a time.
            </p>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** A titled group of fields. */
function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-[0.6875rem] font-semibold uppercase tracking-wide text-ink-subtle">{title}</h3>
      {children}
    </section>
  );
}

/** A label over one control, with optional help text. */
function Labeled({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-xs text-ink-muted">
      {label}
      {children}
      {hint && <span className="text-[11px] leading-snug text-ink-subtle">{hint}</span>}
    </label>
  );
}
